import {useProcessStagesForManualKpisDelete, ManualKpiToUpdate} from '@domain/shifthandover'
import {ExpandedGroupIds} from '@hconnect/common/hooks/useExpandGroups'
import {ProcessStage} from '@hconnect/common/types'
import {Column, MenuButton, MenuConfig} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {DeleteOutlined} from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import {Button} from '@mui/material'
import {ManualKpiWrapper} from '@widgets/kpis'
import {ConfirmationDialog, ErrorDialog} from '@widgets/shift-event-details'
import {AxiosError} from 'axios'
import React, {FC, useCallback, useMemo, useState} from 'react'
import {Control} from 'react-hook-form'
import {Trans, useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {KpiFormData} from '../../types'
import {generateEditColumns} from '../../utils'

import {ManualKpiCreateDialog} from './ManualKpiCreateDialog'

type ManualKpiEditProps = {
  manualKpiList: ManualKpiToUpdate[]
  setManualKpiList: (manualKpiList: ManualKpiToUpdate[]) => void
  processStages: ProcessStage[]
  isProcessStagesLoading: boolean
  openProcessStages: string[]
  setOpenProcessStages: (id: ExpandedGroupIds) => void
  control: Control<KpiFormData>
  updateError: AxiosError | null
  updateReset: () => void
  refetchProcessStages?: () => void
}

export const ManualKpiEdit: FC<ManualKpiEditProps> = ({
  processStages,
  manualKpiList,
  setManualKpiList,
  isProcessStagesLoading,
  openProcessStages,
  setOpenProcessStages,
  control,
  updateError,
  updateReset,
  refetchProcessStages
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {plantId} = useUpmPlantContext()

  const [activeProcessStage, setActiveProcessStage] = useState<string>()
  const [processStageToDelete, setProcessStageToDelete] = useState<ProcessStage>()

  const {
    mutate: deleteProcessStage,
    isPending: isDeletingProcessStage,
    error: deleteProcessStageError,
    reset: resetProcessStageDeletionState
  } = useProcessStagesForManualKpisDelete({
    plantId,
    options: {
      onSuccess: () => {
        setProcessStageToDelete(undefined)
        refetchProcessStages?.()
      }
    }
  })

  const getMenuConfig = useCallback<(processStage: ProcessStage) => MenuConfig>(
    (processStage: ProcessStage) => [
      {
        dataTestId: 'process-stage-delete-button',
        label: t('action.delete'),
        onClick: () => {
          setProcessStageToDelete(processStage)
        },
        icon: <DeleteOutlined sx={{mr: 2, verticalAlign: 'text-bottom'}} />
      }
    ],
    [t]
  )

  const addManualKpi = (data: ManualKpiToUpdate) => {
    if (activeProcessStage) {
      setManualKpiList([...manualKpiList, {...data, processStage: activeProcessStage}])
    }
  }

  const removeManualKpi = useCallback(
    (processStageId: string, name: string) => {
      const newList = manualKpiList.filter(
        (kpi) => !(kpi.name === name && kpi.processStage === processStageId)
      )
      setManualKpiList(newList)
    },
    [manualKpiList, setManualKpiList]
  )

  const columns = useMemo<Column<ManualKpiToUpdate>[]>(
    () => generateEditColumns(t, control, removeManualKpi),
    [t, control, removeManualKpi]
  )

  const kpiNames = useMemo(
    () =>
      manualKpiList
        .filter(({processStage}) => processStage === activeProcessStage)
        .map((kpi) => kpi.name),
    [manualKpiList, activeProcessStage]
  )

  return (
    <>
      <ManualKpiWrapper
        getExpandableListItemAction={(code, name) => (
          <MenuButton
            config={getMenuConfig({code, name})}
            menuDataTestId={`process-stage-more-menu-${code}`}
            data-test-id={`process-stage-more-button-${code}`}
          />
        )}
        getSectionBottomAction={(code) => (
          <Button
            onClick={() => setActiveProcessStage(code)}
            startIcon={<AddIcon />}
            variant="text"
            data-test-id={`performance-new-kpi-button-${code}`}
          >
            {t('performance.label.createNewKpi')}
          </Button>
        )}
        setOpenProcessStages={setOpenProcessStages}
        openProcessStages={openProcessStages}
        isProcessStagesLoading={isProcessStagesLoading}
        processStages={processStages}
        manualKpiList={manualKpiList}
        columns={columns}
      />

      <ManualKpiCreateDialog
        onSave={addManualKpi}
        isDialogOpen={!!activeProcessStage}
        onClose={() => setActiveProcessStage(undefined)}
        existingKpiNames={kpiNames}
      />

      {updateError && (
        <ErrorDialog
          httpCode={updateError.code}
          statusCode={updateError?.response?.status}
          onClose={() => {
            updateReset()
          }}
          message={t('performance.label.saveError')}
          errorText={updateError.message}
        />
      )}
      {deleteProcessStageError && (
        <ErrorDialog
          httpCode={deleteProcessStageError.code}
          onClose={() => {
            resetProcessStageDeletionState()
          }}
          message={t('performance.label.deleteProcessStageError')}
          errorText={deleteProcessStageError.message}
        />
      )}

      {!!processStageToDelete && (
        <ConfirmationDialog
          onDeleteSingle={() => {
            deleteProcessStage(processStageToDelete.code)
          }}
          onClose={() => setProcessStageToDelete(undefined)}
          isSingleLoading={isDeletingProcessStage}
          text={
            <Trans
              i18nkey="performance.label.sureToDelete"
              values={{kpi: processStageToDelete.name}}
            >
              Are you sure you want to delete <strong>{processStageToDelete.name}</strong>? This
              action cannot be undone.
            </Trans>
          }
          isSeriesItem={false}
        />
      )}
    </>
  )
}
